import { createBrowserHistory, createMemoryHistory } from 'history';

type ConfirmationCallback = (result: boolean) => void;
type ConfirmationHandler = (message: string, callback: ConfirmationCallback) => void;

let confirmationHandler: ConfirmationHandler | null = null;

/**
 * Register a handler that will be called instead of `window.confirm` when
 * `history.block()` returns a string.  Pass `null` to clear the handler.
 */
export function setNavigationConfirmation(handler: ConfirmationHandler | null): void {
  confirmationHandler = handler;
}

/**
 * `createBrowserHistory` throws "Browser history needs a DOM" the moment this module is
 * evaluated, so importing it from a module that the node test project can reach kills
 * that whole import graph at collection time — before any assertion, with a stack that
 * points here and not at whoever introduced the edge.
 *
 * This module is now reachable that way: `Auth/rbac/index` -> `useRole` ->
 * `utils/previewOverrideUrl` -> here, because the `?preview_role=` / `?preview_plan=` strip has to
 * go through the app's own history to keep the router in sync. That edge is correct and
 * stays; what has to give is the module-scope throw. In node — where nothing navigates —
 * an inert memory history is the honest stand-in.
 *
 * Every environment that has a DOM (browser, jsdom, Storybook) takes the browser branch
 * exactly as before, so no runtime behaviour changes for anyone. Nor does this hide
 * misuse: node code that actually tries to navigate still fails loudly, because the call
 * sites touch `window.location` themselves.
 */
const createHistory = typeof window !== 'undefined' && window.document ? createBrowserHistory : createMemoryHistory;

const history = createHistory({
  getUserConfirmation(message: string, callback: ConfirmationCallback) {
    if (confirmationHandler) {
      confirmationHandler(message, callback);
    } else {
      // No handler registered — fall back to the default browser confirm.
      callback(window.confirm(message));
    }
  },
});

export default history;
